import { useState, useEffect } from 'react';
import type { QuestTemplateChatFirestore, QuestTemplateFieldToCollect } from '../../services/questTypes';
import { QuestFormFields } from './QuestFormFields';
import { useQuestForm } from './useQuestForm';
import QuestFieldsToCollectManager from '../fields-to-collect/QuestFieldsToCollectManager';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import ErrorDisplay from '../../../../components/ErrorDisplay';

interface ChatSpecificFormData {
    buttonText: string;
    aiPromptGuidance: string;
    aiOpeningStatementGuidance: string;
    aiClosingStatementGuidance: string;
    objective: string;
    fieldsToCollect: QuestTemplateFieldToCollect[];
}

interface ChatQuestFormProps {
    questTemplate?: QuestTemplateChatFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateChatFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateChatFirestore) => void;
    onCancel: () => void;
}

export const ChatQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: ChatQuestFormProps) => {
    // Use the common quest form hook
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    // Chat-specific form data
    const [specificFormData, setSpecificFormData] = useState<ChatSpecificFormData>({
        buttonText: '',
        aiPromptGuidance: '',
        aiOpeningStatementGuidance: '',
        aiClosingStatementGuidance: '',
        objective: '',
        fieldsToCollect: [],
    });

    // Track unsaved changes in fields to collect
    const [hasUnsavedFieldChanges, setHasUnsavedFieldChanges] = useState(false);

    // Initialize chat-specific data
    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                buttonText: questTemplate.buttonText || '',
                aiPromptGuidance: questTemplate.aiPromptGuidance,
                aiOpeningStatementGuidance: questTemplate.aiOpeningStatementGuidance,
                aiClosingStatementGuidance: questTemplate.aiClosingStatementGuidance,
                objective: questTemplate.objective,
                fieldsToCollect: questTemplate.fieldsToCollect,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean | QuestTemplateFieldToCollect[]) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean | QuestTemplateFieldToCollect[]) => {
        // Check if it's a common field or specific field
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.objective.trim()) {
            return 'Objective is required';
        }
        if (hasUnsavedFieldChanges) {
            return 'Please save or cancel the field you are currently editing before submitting the form';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateChatFirestore>): QuestTemplateChatFirestore => {
        const maxProgress = specificFormData.fieldsToCollect.length > 0 ? specificFormData.fieldsToCollect.length : 1;

        const questTemplateData: QuestTemplateChatFirestore = {
            ...baseData,
            type: 'chat',
            maxProgress,
            aiPromptGuidance: specificFormData.aiPromptGuidance,
            aiOpeningStatementGuidance: specificFormData.aiOpeningStatementGuidance,
            aiClosingStatementGuidance: specificFormData.aiClosingStatementGuidance,
            objective: specificFormData.objective,
            fieldsToCollect: specificFormData.fieldsToCollect,
        } as QuestTemplateChatFirestore;

        // Only include buttonText if it has a value
        if (specificFormData.buttonText && specificFormData.buttonText.trim()) {
            questTemplateData.buttonText = specificFormData.buttonText;
        }

        return questTemplateData;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="chat"
                disableProgressType={false}
                onChange={handleChange}
            />


            <QuestFormSectionDivider title="Chat-specific fields" />

            {/* Chat-specific fields */}
            <div>
                <label htmlFor="buttonText" className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                </label>
                <input
                    type="text"
                    id="buttonText"
                    value={specificFormData.buttonText}
                    onChange={(e) => handleChange('buttonText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Start Chat"
                />
            </div>

            <div>
                <label htmlFor="objective" className="block text-sm font-medium text-gray-700 mb-1">
                    Objective *
                </label>
                <textarea
                    id="objective"
                    value={specificFormData.objective}
                    onChange={(e) => handleChange('objective', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter objective"
                    rows={3}
                    required
                />
                <p className="text-xs text-gray-500 mt-1">
                    This is the objective of the chat. This will be used to guide the AI's questions and responses.
                </p>
            </div>

            <div>
                <label htmlFor="aiPromptGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Prompt Guidance
                </label>
                <textarea
                    id="aiPromptGuidance"
                    value={specificFormData.aiPromptGuidance}
                    onChange={(e) => handleChange('aiPromptGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI prompt guidance"
                    rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                    This is the prompt that will be used to supplement the base quest prompt.
                </p>
            </div>

            <div>
                <label htmlFor="aiOpeningStatementGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Opening Statement Guidance
                </label>
                <textarea
                    id="aiOpeningStatementGuidance"
                    value={specificFormData.aiOpeningStatementGuidance}
                    onChange={(e) => handleChange('aiOpeningStatementGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI opening statement guidance"
                    rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                    This is the opening statement that will be used to open the chat.
                </p>
            </div>

            <div>
                <label htmlFor="aiClosingStatementGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Closing Statement Guidance
                </label>
                <textarea
                    id="aiClosingStatementGuidance"
                    value={specificFormData.aiClosingStatementGuidance}
                    onChange={(e) => handleChange('aiClosingStatementGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI closing statement guidance"
                    rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                    This is the closing statement that will be used to close the chat.
                </p>
            </div>

            <QuestFieldsToCollectManager
                fields={specificFormData.fieldsToCollect}
                onChange={(fields: QuestTemplateFieldToCollect[]) => handleChange('fieldsToCollect', fields)}
                onUnsavedChangesChange={setHasUnsavedFieldChanges}
            />

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
